import { unique } from '@vaaas/fpts/array';
import {
    disable_ui_element,
    enable_ui_element,
} from './non_graphical_ui_actions';
import {
    uil_can_deflate,
    uil_can_inflate,
    uil_get_body_of_edge,
    uil_get_body_of_face,
    uil_get_face_connected_components,
    uil_get_faces_of_edge,
    uil_is_object_visible,
    uil_make_hole_or_handle_multi,
} from './ui_logic';
import App from './application-container';
import {
    can_bridge_edges,
    merge_bodies_on_edge_selecting_edges_or_faces,
    stitch_edges_of_open_faces_on_same_body,
    uid_deselect_all_objects,
    uid_fill_faces,
    uid_inflate_deflate,
} from './ui_display';
import { shift_pressed } from './keypress';
import type { Body, Face, Vertex } from '@phi/jcore';
import { Option } from '@vaaas/fpts/option';
import { vertices_of_face } from './lattice';

const ref_sketch_mode = () => App.state.editing_reference_sketches;

export function handle(): undefined | ((modifier: boolean) => void) {
    // ----- Handle (bridge) button ----------
    // We separate two separate uses. If only edges are selected, we check if this can be a "bridge open surfaces" command.
    // Otherwise we go for the old handle usage.
    if (ref_sketch_mode())
        return undefined;
    else if (uid_selected_edges.length > 0 && uid_selected_faces.length == 0) {
        // Examine bridge open surfaces usage (lofting style).
        if (can_bridge_edges(uid_selected_edges.keys()))
            return () => uid_fill_faces();
        else
            return;
    } else {
        // Examine bridge faces (handle) usage.
        const handle_bodies: Body[] = [];
        uid_selected_faces.keys().forEach((fid) => {
            if (handle_bodies.indexOf(uil_get_body_of_face(fid)) == -1)
                handle_bodies.push(uil_get_body_of_face(fid));
        });

        // Consider anchor vertices if only 2 are selected, and not more or less.
        let v1_id: Option<Vertex> | 0 = 0;
        let v2_id: Option<Vertex> | 0 = 0;
        if (uid_selected_vertices.length == 2) {
            v1_id = uid_selected_vertices.getKeyAtIndex(0);
            v2_id = uid_selected_vertices.getKeyAtIndex(1);
        }

        // Consider hole or handle on one body
        // All selected faces must belong in the same body
        if (handle_bodies.length == 1 && uid_selected_faces.length > 1) {
            // Check if selected faces form 2 connected components.
            const face_connected_components = uil_get_face_connected_components(uid_selected_faces.keys());
            if (!face_connected_components)
                return undefined;
            else if (face_connected_components.length == 2) {
                // If we only have 2 face connected components selected in the same body, we can do the hole.
                const face1_ids = face_connected_components[0]!;
                const face2_ids = face_connected_components[1]!;

                let v1ok = 1;
                let v2ok = 2;
                if (v1_id && v2_id) {
                    v1ok = 0;
                    v2ok = 0;
                    // Check that pair of anchor vertices are correctly placed on faces
                    face1_ids.forEach((fid) => {
                        if (vertices_of_face(fid).includes(v1_id as any))
                            v1ok = 1;
                        if (vertices_of_face(fid).includes(v2_id as any))
                            v2ok = 1;
                    });
                    face2_ids.forEach((fid) => {
                        if (vertices_of_face(fid).includes(v1_id as any))
                            v1ok = 2;
                        if (vertices_of_face(fid).includes(v2_id as any))
                            v2ok = 2;
                    });
                }
                if (v1ok != 0 && v2ok != 0 && v1ok != v2ok)  {
                    if (v1ok == 2 && v2ok ==1) {
                        const vtmp = v1_id;
                        v1_id = v2_id;
                        v2_id = vtmp;
                    }

                    return (modifier) => {
                        uid_deselect_all_objects();
                        uil_make_hole_or_handle_multi(face1_ids, face2_ids, false, v1_id as Vertex, v2_id as Vertex, false, modifier);
                    };
                } else
                    return;
            } else
                return;
        } else if (handle_bodies.length == 2) {
            // Check if selected faces form 2 connected components.
            const face_connected_components = uil_get_face_connected_components(uid_selected_faces.keys());
            if (!face_connected_components) {
                return;
            } else if (face_connected_components.length == 2) {
                // If we only have 2 face connected components, they must be on the two bodies, so we're ok.
                const face1_ids = face_connected_components[0]!;
                const face2_ids = face_connected_components[1]!;

                let v1ok = 1;
                let v2ok = 2;
                if (v1_id && v2_id) {
                    v1ok = 0;
                    v2ok = 0;
                    // Check that pair of anchor vertices are correctly placed on faces
                    face1_ids.forEach((fid) => {
                        if (vertices_of_face(fid).includes(v1_id as any))
                            v1ok = 1;
                        if (vertices_of_face(fid).includes(v2_id as any))
                            v2ok = 1;
                    });
                    face2_ids.forEach((fid) => {
                        if (vertices_of_face(fid).includes(v1_id as any))
                            v1ok = 2;
                        if (vertices_of_face(fid).includes(v2_id as any))
                            v2ok = 2;
                    });
                }
                if (v1ok != 0 && v2ok != 0 && v1ok != v2ok)  {
                    if (v1ok == 2 && v2ok ==1) {
                        const vtmp = v1_id;
                        v1_id = v2_id;
                        v2_id = vtmp;
                    }
                    return (modifier) => {
                        uid_deselect_all_objects();
                        uil_make_hole_or_handle_multi(face1_ids, face2_ids, false, v1_id as Vertex, v2_id as Vertex, false, modifier);
                    };
                } else {
                    return;
                }
            } else {
                return;
            }
        } else {
            return;
        }
    }
}

export function stitch(): undefined | (() => void) {
    if (ref_sketch_mode())
        return;
    else if (
        uid_selected_edges.length == 2
        && uil_get_body_of_edge(uid_selected_edges.getKeyAtIndex(0)!) != uil_get_body_of_edge(uid_selected_edges.getKeyAtIndex(1)!)
    )
        return () =>
            merge_bodies_on_edge_selecting_edges_or_faces(
                uid_selected_edges.getKeyAtIndex(0)!,
                uid_selected_edges.getKeyAtIndex(1)!,
                0,
                0,
            );
    else if (
        uid_selected_edges.length == 0
        && uid_selected_faces.length == 2
        && uid_selected_vertices.length == 0
        && uil_get_body_of_face(uid_selected_faces.getKeyAtIndex(0)!) != uil_get_body_of_face(uid_selected_faces.getKeyAtIndex(1)!)
    )
        return () => merge_bodies_on_edge_selecting_edges_or_faces(
            0,
            0,
            uid_selected_faces.getKeyAtIndex(0)!,
            uid_selected_faces.getKeyAtIndex(1)!,
        );
    else if (
        uid_selected_edges.length == 2
        && uil_get_body_of_edge(uid_selected_edges.getKeyAtIndex(0)!) == uil_get_body_of_edge(uid_selected_edges.getKeyAtIndex(1)!)
        && (
            (uil_is_object_visible(uil_get_faces_of_edge(uid_selected_edges.getKeyAtIndex(0)!)[0]) &&
             !uil_is_object_visible(uil_get_faces_of_edge(uid_selected_edges.getKeyAtIndex(0)!)[1])) ||
            (uil_is_object_visible(uil_get_faces_of_edge(uid_selected_edges.getKeyAtIndex(0)!)[1]) &&
             !uil_is_object_visible(uil_get_faces_of_edge(uid_selected_edges.getKeyAtIndex(0)!)[0])))
        && (
            (uil_is_object_visible(uil_get_faces_of_edge(uid_selected_edges.getKeyAtIndex(0)!)[0]) &&
             !uil_is_object_visible(uil_get_faces_of_edge(uid_selected_edges.getKeyAtIndex(0)!)[1])) ||
            (uil_is_object_visible(uil_get_faces_of_edge(uid_selected_edges.getKeyAtIndex(0)!)[1]) &&
             !uil_is_object_visible(uil_get_faces_of_edge(uid_selected_edges.getKeyAtIndex(0)!)[0])))
    )
        return () => stitch_edges_of_open_faces_on_same_body(
            uid_selected_edges.getKeyAtIndex(0)!,
            uid_selected_edges.getKeyAtIndex(1)!,
            0,
            0,
        );
    else if (
        uid_selected_edges.length == 0
        && uid_selected_vertices.length == 0
        && uid_selected_faces.length == 2
        && uil_get_body_of_face(uid_selected_faces.getKeyAtIndex(0)!) == uil_get_body_of_face(uid_selected_faces.getKeyAtIndex(1)!)
    )
        return () => stitch_edges_of_open_faces_on_same_body(
            0,
            0,
            uid_selected_faces.getKeyAtIndex(0)!,
            uid_selected_faces.getKeyAtIndex(1)!,
        );

    else
        return;
}

export function hole(): undefined | (() => void) {
    const hole_bodies = unique(
        uid_selected_faces.keys()
            .map(uil_get_body_of_face)
    );

    // Consider anchor vertices if only 2 are selected, and not more or less.
    let v1_id: Option<Vertex> | 0 = 0;
    let v2_id: Option<Vertex> | 0 = 0;
    if (uid_selected_vertices.length == 2) {
        v1_id = uid_selected_vertices.getKeyAtIndex(0);
        v2_id = uid_selected_vertices.getKeyAtIndex(1);
    }

    if (hole_bodies.length !== 1 || uid_selected_faces.length === 0)
        return;

    // Check if selected faces form 2 connected components.
    const face_connected_components = uil_get_face_connected_components(uid_selected_faces.keys());
    if (!face_connected_components)
        return;
    if (face_connected_components.length !== 2)
        return;

    // If we only have 2 face connected components selected in the same body, we can do the hole.
    const [face1_ids, face2_ids] = face_connected_components as [Face[], Face[]];

    let v1ok = 1;
    let v2ok = 2;
    if (v1_id && v2_id) {
        // Check that pair of anchor vertices are correctly placed on faces
        for (const fid of face1_ids) {
            if (vertices_of_face(fid).indexOf(v1_id) !=-1)
                v1ok = 1;
            if (vertices_of_face(fid).indexOf(v2_id) !=-1)
                v2ok = 1;
        }
        for (const fid of face2_ids) {
            if (vertices_of_face(fid).indexOf(v1_id) !=-1)
                v1ok = 2;
            if (vertices_of_face(fid).indexOf(v2_id) !=-1)
                v2ok = 2;
        }
    }

    if (v1ok != 0 && v2ok != 0 && v1ok != v2ok)  {
        if (v1ok == 2 && v2ok ==1) {
            const vtmp = v1_id;
            v1_id = v2_id;
            v2_id = vtmp;
        }

        return () => {
            uid_deselect_all_objects();
            uil_make_hole_or_handle_multi(face1_ids, face2_ids, true, v1_id as Vertex, v2_id as Vertex, false, shift_pressed());
        };
    } else
        return;
}

export function inflate_deflate() {
    if (!ENABLE_INFLATE_DEFLATE)
        return;

    let make_inflate_button_active = false;
    let make_deflate_button_active = false;
    if (uid_selected_faces.length > 0) {
        uid_selected_faces.keys().forEach((f) => {
            if (uil_can_inflate(f) > 0)
                make_inflate_button_active = true;
            if (uil_can_deflate(f) > 0)
                make_deflate_button_active = true;
        });
    }

    if (ENABLE_INFLATE_DEFLATE) {
        if (make_inflate_button_active)
            enable_ui_element('#inflate-face-button', () => uid_inflate_deflate(true));
        else
            disable_ui_element('#inflate-face-button');
    }
}
